import { MaintenancePlanWithDetails } from "@boark/shared/types/maintenance-plan-with-details";
import { useMemo, useState } from "react";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { FullscreenToggle } from "../../components/fullscreen";
import { MaintenancePlanImportModal } from "../../components/maintenance-plan-import-modal";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetMaintenancePlans } from "../../queries/maintenance-plan";
import { formatMaintenancePlanCodeLabel } from "../../services/maintenance-plan-group";
import { formatPropertyPartLabel } from "../../services/property-part";
import { useLocale } from "../../stores/locale";
import { formatPrice } from "../../utils/format-price";
import {
  formatMaintenancePlanLastAction,
  formatYearMonth,
  getMaintenancePlanNextActionDate,
} from "../../utils/maintenance-plan-schedule";
import { recordIdToId } from "../../utils/record-id-to-id";
import { MaintenancePlanCreate } from "./maintenance-plan-create";
import { MaintenancePlanDelete } from "./maintenance-plan-delete";
import { MaintenancePlanEdit } from "./maintenance-plan-edit";
import { MaintenancePlanView } from "./maintenance-plan-view";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

type MaintenancePlanRow = MaintenancePlanWithDetails & {
  nextActionDate?: Date;
};

export function MaintenancePlanList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [importOpen, setImportOpen] = useState(false);
  const { data: maintenancePlans, isLoading: isMaintenancePlansLoading } = useGetMaintenancePlans();

  const rows = useMemo<MaintenancePlanRow[]>(
    () =>
      (maintenancePlans ?? []).map((plan) => ({
        ...plan,
        nextActionDate: getMaintenancePlanNextActionDate({
          createdAt: plan.createdAt,
          yearlyFrequency: plan.yearlyFrequency,
          lastActionDate: plan.lastActionDate,
        }),
      })),
    [maintenancePlans],
  );

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<MaintenancePlanCreate />} path="/create" />
        <Route element={<MaintenancePlanView />} path="/:id" />
        <Route element={<MaintenancePlanEdit />} path="/:id/edit" />
        <Route element={<MaintenancePlanDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.maintenance.planTab}</H1>
          <div className="flex gap-2">
            <FullscreenToggle />
            <Button type="button" onClick={() => setImportOpen(true)}>
              {t.maintenance.importPlan}
            </Button>
            <Button route="/admin/maintenance/plan/create">
              {t.maintenance.createPlan}
            </Button>
          </div>
        </div>
        {importOpen && (
          <MaintenancePlanImportModal onClose={() => setImportOpen(false)} />
        )}
        <Table
          data={rows}
          loading={isMaintenancePlansLoading}
          viewRouteButton={(row) =>
            `/admin/maintenance/plan/${recordIdToId(row.id)}`
          }
          editRouteButton={(row) =>
            `/admin/maintenance/plan/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/maintenance/plan/${recordIdToId(row.id)}/delete`
          }
          rowClassName={(row) =>
            row.nextActionDate && row.nextActionDate < new Date()
              ? "bg-red-100"
              : undefined
          }
          sortBy={searchParams.get("sortBy") || "maintenancePlanCode.code"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column
            body={(row: MaintenancePlanRow) =>
              formatMaintenancePlanCodeLabel({
                code: row.maintenancePlanCode.code,
                groupName: row.maintenancePlanCode.maintenancePlanGroup.name,
              })
            }
            field="maintenancePlanCode.code"
            filterableBy="select"
            filterOptions={(data: MaintenancePlanRow[]) => {
              const options = new Map<string, string>();
              data.forEach((plan) => {
                options.set(
                  plan.maintenancePlanCode.code,
                  formatMaintenancePlanCodeLabel({
                    code: plan.maintenancePlanCode.code,
                    groupName: plan.maintenancePlanCode.maintenancePlanGroup.name,
                  }),
                );
              });
              return Array.from(options.entries()).map(([value, label]) => ({
                label,
                value,
              }));
            }}
          >
            {t.maintenance.code}
          </Column>
          <Column field="description" filterableBy="text">
            {t.maintenance.description}
          </Column>
          <Column
            body={(row: MaintenancePlanRow) =>
              formatPropertyPartLabel(row.propertyPart)
            }
            field="propertyPart.name"
            filterableBy="select"
            filterOptions={(data: MaintenancePlanRow[]) => {
              const options = new Map<string, string>();
              data.forEach((plan) => {
                options.set(
                  plan.propertyPart.name,
                  formatPropertyPartLabel(plan.propertyPart),
                );
              });
              return Array.from(options.entries()).map(([value, label]) => ({
                label,
                value,
              }));
            }}
          >
            {t.maintenance.propertyPart}
          </Column>
          <Column
            body={(row: MaintenancePlanRow) =>
              row.cost !== undefined ? formatPrice(row.cost) : "-"
            }
            field="cost"
            filterableBy="text"
          >
            {t.maintenance.cost}
          </Column>
          <Column
            body={(row: MaintenancePlanRow) => row.yearlyFrequency ?? "-"}
            field="yearlyFrequency"
            filterableBy="select"
            filterOptions={(data: MaintenancePlanRow[]) => {
              const values = Array.from(
                new Set(
                  data
                    .map((plan) => plan.yearlyFrequency)
                    .filter((value): value is number => value !== undefined),
                ),
              ).sort((a, b) => a - b);
              return values.map((value) => ({
                label: value.toString(),
                value,
              }));
            }}
          >
            {t.maintenance.yearlyFrequency}
          </Column>
          <Column
            body={(row: MaintenancePlanRow) =>
              formatMaintenancePlanLastAction(row.lastActionDate)
            }
            field="lastActionDate"
            filterableBy="date"
          >
            {t.maintenance.lastAction}
          </Column>
          <Column
            body={(row: MaintenancePlanRow) =>
              row.nextActionDate ?
                formatYearMonth(row.nextActionDate)
                : "-"
            }
            field="nextActionDate"
            filterableBy="date"
            sortCallback={(a, b, _field, direction) => {
              const dateA = a.nextActionDate;
              const dateB = b.nextActionDate;
              if (!dateA && !dateB) {
                return 0;
              }
              if (!dateA) {
                return 1;
              }
              if (!dateB) {
                return -1;
              }
              const result = dateA.getTime() - dateB.getTime();
              return direction === "asc" ? result : -result;
            }}
          >
            {t.maintenance.nextAction}
          </Column>
          <Column field="actionOverdue"
          sortCallback={(a, b, _field, direction) => {
            const dateA = a.nextActionDate;
            const dateB = b.nextActionDate;
            const isOverdueA = dateA && dateA < new Date();
            const isOverdueB = dateB && dateB < new Date();
            if (isOverdueA && !isOverdueB) {
              return direction === "asc" ? -1 : 1;
            }
            if (!isOverdueA && isOverdueB) {
              return direction === "asc" ? 1 : -1;
            }
            return 0;
          }}
          filterableBy="select"
          filterOptions={() => [
            { label: t.yes, value: "yes" },
            { label: t.no, value: "no" },
          ]}
          onFilter={(value, row) => {
            const isOverdue = row.nextActionDate && row.nextActionDate < new Date();
            if (value === "yes") {
              return isOverdue;
            }
            if (value === "no") {
              return !isOverdue;
            }
            return true;
          }}
            body={(row: MaintenancePlanRow) =>
              (row.nextActionDate &&
                row.nextActionDate && row.nextActionDate < new Date() && (
                  <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 mr-1" />
                )
              )}
          bodyClassName="!text-center"
          >
            {t.maintenance.requireAction}
          </Column>
        </Table>
      </div>
    </>
  );
}
